import React, { useState, useEffect, useRef } from 'react';
import {
  Compass,
  CheckCircle2,
  Maximize2,
  Minimize2,
  LandPlot,
  Map,
  Leaf,
  Users,
  ArrowRight,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  ChevronUp,
  ChevronDown,
  X,
  HelpCircle,
  Sparkles,
  Lock,
  ThermometerSun,
  CalendarDays,
  FolderOpen,
  FileText,
  Play,
  RotateCcw,
  Tag,
  Mountain,
  GripHorizontal,
  Layers,
  Tractor,
} from 'lucide-react';
import type { Page, AreaType } from '../types';
import { useDialogA11y } from '../lib/useDialogA11y';

export type GuideTier = 'free' | 'semipro' | 'pro';

export interface TourTrackingProps {
  page: Page;
  fieldCount: number;
  hasZoneField?: boolean;
  hasPoi?: boolean;
  cadastralEnabled?: boolean;
  markblokkeEnabled?: boolean;
  selectedFieldHasSeed?: boolean;
  cartCount?: number;
  isDrawing?: boolean;
}

export interface GuideStep {
  id: string;
  page: Page;
  title: string;
  shortDesc: string;
  tip: string;
  actionLabel?: string;
  actionType?:
    | 'fullscreen'
    | 'draw_field'
    | 'navigate'
    | 'catalog'
    | 'upgrade'
    | 'try_markblok'
    | 'try_cadastral'
    | 'try_arealer'
    | 'try_punkter';
  targetFeature?: string;
  targetSelector?: string;
}

export const GUIDE_STEPS_FREE: GuideStep[] = [
  {
    id: 'free-overview',
    page: 'overview',
    title: 'Dit Revir & Overblik',
    shortDesc:
      'Her samles dit revir, dine optegnede vildtagre, dit samlede areal og dine frøblandinger.',
    tip: '💡 Tip: Angiv altid revirets præcise adresse. Så flyver kortet automatisk direkte til din ejendom ved hvert login.',
    actionLabel: 'Gå til Overblik',
    actionType: 'navigate',
    targetSelector: '[data-guide="nav-overview"]',
  },
  {
    id: 'free-map-fullscreen',
    page: 'map',
    title: 'Mine marker & Fuldskærm',
    shortDesc: 'Kortet giver dig detaljeret overblik på GeoDanmark forårs-luftfotos uden skygger.',
    tip: '💡 Anbefaling: Slå ALTID fuldskærm til, når du tegner eller inspicerer marker, så du får maksimalt arbejdsareal og præcision.',
    actionLabel: 'Prøv Fuldskærm',
    actionType: 'fullscreen',
    targetSelector: '[data-guide="btn-fullscreen"]',
  },
  {
    id: 'free-map-draw',
    page: 'map',
    title: 'Tegn din første vildtager',
    shortDesc:
      'Vælg "Vildtager" i toppen. Klik langs skovbryn eller remiser og dobbeltklik for at afslutte.',
    tip: '💡 Arealberegning: Dit areal i hektar (ha) beregnes live i realtid — og gemmes permanent på dit revir, så intet går tabt!',
    actionLabel: 'Start optegning',
    actionType: 'draw_field',
    targetSelector: '[data-guide="action-vildtager"]',
  },
  {
    id: 'free-map-layers',
    page: 'map',
    title: 'Kortlag & Luftfotos',
    shortDesc:
      'Skift nemt mellem GeoDanmark Forårs-ortofoto, Skærmkort og Terræn via lag-knappen i højre side.',
    tip: '🔒 Semi-Pro låser op for officielle matrikelskel fra Dataforsyningen og live jordtemperatur til såning.',
    actionLabel: 'Åbn kortlag',
    actionType: 'try_cadastral',
    targetSelector: '[data-guide="btn-kortlag"]',
  },
  {
    id: 'free-catalog',
    page: 'catalog',
    title: 'Frøblandinger & Beregner',
    shortDesc:
      'Vælg den rigtige blanding til marken. Plotwise udregner nøjagtigt posebehov og overskydende hektar.',
    tip: '🌾 Hunters Corn måles i kerner/ha (100.000 kerner/ha) – andre i kg/ha. Bestil direkte hos Faunamaster med 5% BEINER-rabat!',
    actionLabel: 'Se frøblandinger',
    actionType: 'navigate',
    targetSelector: '[data-guide="nav-catalog"]',
  },
  {
    id: 'free-advisor',
    page: 'catalog',
    title: 'Er du i tvivl? & Rådgivning',
    shortDesc:
      'Brug frøguiden til at finde blandingen til din vildttype, eller kontakt rådgiver Emil Beiner.',
    tip: '🤝 Du kan altid kontakte os under fanen "Rådgivning" for personlig vejledning i biotopplanlægning.',
    actionLabel: 'Gå til Rådgivning',
    actionType: 'navigate',
    targetSelector: '[data-guide="nav-advisor"]',
  },
];

export const GUIDE_STEPS_SEMIPRO: GuideStep[] = [
  {
    id: 'semi-overview',
    page: 'overview',
    title: '1. Udvidet Revir Dashboard',
    shortDesc:
      'Få samlet overblik over markudvikling, sæsoner, såbedsklarhed og nøgletal for hele konsortiet.',
    tip: '💡 Dashboardet holder automatisk styr på dine vildtagre, læhegn og biotopfordeling.',
    actionLabel: 'Gå til Overblik',
    actionType: 'navigate',
    targetSelector: '[data-guide="nav-overview"]',
  },
  {
    id: 'semi-map-fullscreen',
    page: 'map',
    title: '2. Mine marker & Det store kort',
    shortDesc:
      'Åbn kortet i fuldskærm for maksimalt arbejdsareal og præcision på de skyfrie forårs-luftfotos.',
    tip: '💡 Fuldskærmsanbefaling: Kør altid fuldskærm, når du planlægger, tegner eller gennemgår reviret.',
    actionLabel: 'Kør Fuldskærm',
    actionType: 'fullscreen',
    targetSelector: '[data-guide="btn-fullscreen"]',
  },
  {
    id: 'semi-cadastral',
    page: 'map',
    title: '3. Matrikelskel fra Dataforsyningen',
    shortDesc:
      'Slå officielle matrikelskel til med ét klik for at kende nabogrænser og matrikelnumre på forårs-ortofotoet.',
    tip: '💡 Ingen gætterier ved nabogrænser: Du kan se samtlige matrikelnumre og skelgrænser knivskarpt.',
    actionLabel: 'Prøv denne funktion',
    actionType: 'try_cadastral',
    targetSelector: '[data-guide="btn-kortlag"]',
  },
  {
    id: 'semi-map-draw',
    page: 'map',
    title: '4. Optegn vildtager med live areal',
    shortDesc:
      'Klik på "Vildtager" og tegn langs remisen. Dit areal i hektar (ha) beregnes live og gemmes på reviret.',
    tip: '💡 Permanent revir: Alle dine optegnede marker forbliver gemt i din revirkonto, så dit arbejde aldrig går tabt.',
    actionLabel: 'Tegn vildtager',
    actionType: 'draw_field',
    targetSelector: '[data-guide="action-vildtager"]',
  },
  {
    id: 'semi-zones',
    page: 'map',
    title: '5. Remiser, læhegn & græsstriber',
    shortDesc:
      'Vælg "Arealer ▾" for at oprette biologiske zoner som remiser, brak og læhegn med automatisk 6 m bredde.',
    tip: '🌳 Zonerne hjælper dig med at planlægge vildtets dækning og korridorer på tværs af reviret.',
    actionLabel: 'Prøv denne funktion',
    actionType: 'try_arealer',
    targetSelector: '[data-guide="action-arealer"]',
  },
  {
    id: 'semi-poi',
    page: 'map',
    title: '6. Jagt-POIs & Skydetårne',
    shortDesc:
      'Vælg "Punkter ▾" for at placere skydetårne med skudvinkel, stiger, foderpladser og vildtkameraer.',
    tip: '🎯 Flyt nemt POIs med træk-og-slip og tilknyt noter, observationer eller fotos til hvert punkt.',
    actionLabel: 'Prøv denne funktion',
    actionType: 'try_punkter',
    targetSelector: '[data-guide="action-punkter"]',
  },
  {
    id: 'semi-seeding',
    page: 'seeding',
    title: '7. Så-barometer & Jordtemperatur',
    shortDesc:
      'Overvåg realtids jordtemperatur i 0-7 cm såbedsdybde og få 14-dages mikroklimaprognose.',
    tip: '🌱 Få agronomiske anbefalinger for hver mark, så du sår på det optimale tidspunkt for hurtig spiring.',
    actionLabel: 'Åbn Så-barometer',
    actionType: 'navigate',
    targetSelector: '[data-guide="nav-seeding"]',
  },
  {
    id: 'semi-plans',
    page: 'plans',
    title: '8. Flerårige Plejeplaner',
    shortDesc:
      'Opret plejeplaner på tværs af forår og efterår. Hold styr på jordbehandling, afgrødevalg og status.',
    tip: '📋 Giver konsortiet og lodsejeren et professionelt overblik over sæsonens vildtpleje.',
    actionLabel: 'Se Plejeplaner',
    actionType: 'navigate',
    targetSelector: '[data-guide="nav-plans"]',
  },
  {
    id: 'semi-season-plans',
    page: 'plans',
    title: '9. Sæsonplanlægning & Såbede',
    shortDesc:
      'Gennemgå sæsonens planlagte marker, forventet såtidspunkt og forberedelse af såbedene.',
    tip: '📋 Få samlet overblik over forårets og efterårets etableringer.',
    actionLabel: 'Se Plejeplaner',
    actionType: 'navigate',
    targetSelector: '[data-guide="nav-plans"]',
  },
  {
    id: 'semi-catalog',
    page: 'catalog',
    title: '10. Frøblandinger & Faunamaster kurv',
    shortDesc:
      'Vælg de rigtige blandinger. Plotwise udregner nøjagtigt posebehov og overskydende hektar med 5% rabat.',
    tip: '🌾 Hunters Corn måles i 100.000 kerner/ha. Bestil direkte med BEINER-koden for hurtig levering.',
    actionLabel: 'Se Frøblandinger',
    actionType: 'navigate',
    targetSelector: '[data-guide="nav-catalog"]',
  },
];

export const GUIDE_STEPS_PRO: GuideStep[] = [
  {
    id: 'pro-overview',
    page: 'overview',
    title: '1. Professionel Revirforvaltning',
    shortDesc:
      'Fuldstændig styring af godser, store jagtvæsener og rådgiver-klienter i ét samlet dashboard.',
    tip: '👑 Udviklet til godsejere, herregårdsskytter og professionelle vildtforvaltere i Danmark.',
    actionLabel: 'Gå til Overblik',
    actionType: 'navigate',
    targetSelector: '[data-guide="nav-overview"]',
  },
  {
    id: 'pro-map-fullscreen',
    page: 'map',
    title: '2. Professionelt GIS-arbejdsbord',
    shortDesc:
      'Fuldskærmskortet er dit primære arbejdsbord med fuld kontrol over kortlag, skel og markblokke.',
    tip: '💡 Fuldskærm sikrer maksimal præcision, når du opmåler biotopparceller for godser og lodsejere.',
    actionLabel: 'Kør Fuldskærm',
    actionType: 'fullscreen',
    targetSelector: '[data-guide="btn-fullscreen"]',
  },
  {
    id: 'pro-markblokke',
    page: 'map',
    title: '3. Landbrugsstyrelsens Markblokke',
    shortDesc:
      'Hent officielle markblokke direkte fra Landbrugsstyrelsen. Klik på en markblok for at se bloknummer og støtteberettiget areal.',
    tip: '🚜 Konvertér officielle markblokke til revirmarker med ét enkelt klik — sparer timer af manuelt tegnearbejde!',
    actionLabel: 'Prøv denne funktion',
    actionType: 'try_markblok',
    targetSelector: '[data-guide="action-markblok"]',
  },
  {
    id: 'pro-cadastral',
    page: 'map',
    title: '4. Matrikelskel & Ejergrænser',
    shortDesc:
      'Kombinér matrikelskel fra Dataforsyningen og markblokke på skyfrie GeoDanmark forårs-ortofotos.',
    tip: '🗺️ Sikrer millimeterpræcis afgrænsning af jagtretten, forpagtninger og ejendomsgrænser.',
    actionLabel: 'Prøv denne funktion',
    actionType: 'try_cadastral',
    targetSelector: '[data-guide="btn-kortlag"]',
  },
  {
    id: 'pro-precision-draw',
    page: 'map',
    title: '5. Præcisionstegning: Vildtager & Parceller',
    shortDesc:
      'Optegn præcise vildtagre og biotopparceller. Arealet i hektar (ha) beregnes i realtid med Turf.js.',
    tip: '💡 Gemt i skyen: Alle markgeometrier synkroniseres direkte og sikkert til din PostgreSQL-database.',
    actionLabel: 'Prøv optegning',
    actionType: 'draw_field',
    targetSelector: '[data-guide="action-vildtager"]',
  },
  {
    id: 'pro-zones-buffer',
    page: 'map',
    title: '6. Remiser & Læhegn med buffer',
    shortDesc:
      'Indtegn læhegn og vildtremiser via "Arealer ▾". Systemet udregner automatisk bufferzoner og korridorer.',
    tip: '🌳 Vigtigt for biotopplanlægning: Læhegn og remiser giver point i Miljøstyrelsens biotopscorecard.',
    actionLabel: 'Prøv denne funktion',
    actionType: 'try_arealer',
    targetSelector: '[data-guide="action-arealer"]',
  },
  {
    id: 'pro-biotope',
    page: 'plans',
    title: '7. Biotopplan & Miljøstyrelsen',
    shortDesc:
      'Komplet scorecard, kvoteberegner og pointopgørelse til Miljøstyrelsens krav for fugleudsætning.',
    tip: '🦆 Sikrer at reviret opfylder pointkravene for agerhøns, fasaner og gråænder automatisk.',
    actionLabel: 'Åbn Plejeplaner',
    actionType: 'navigate',
    targetSelector: '[data-guide="nav-plans"]',
  },
  {
    id: 'pro-clients',
    page: 'clients',
    title: '8. Klientstyring ("Mine kunder")',
    shortDesc:
      'For rådgivere: Hop ind i kundens arbejdsplads med 0 sekunders forsinkelse og tegn/redigér på deres vegne.',
    tip: '⚡ Rådgiver Emil Beiner og godskontorer bruger dette til at yde lynhurtig kundersupport og planlægning.',
    actionLabel: 'Gå til Mine kunder',
    actionType: 'navigate',
    targetSelector: '[data-guide="nav-clients"]',
  },
  {
    id: 'pro-catalog-machine',
    page: 'catalog',
    title: '9. Frø- & Maskinoptimering',
    shortDesc:
      'Officielle udsædsmængder, såbedsanbefalinger og direkte Faunamaster maskin-indstillinger.',
    tip: '🌾 Hunters Corn måles i kerner (100.000 kerner/ha, 25.000 kerner/pose). Bestil med 5% BEINER-rabat!',
    actionLabel: 'Gå til Frøblandinger',
    actionType: 'navigate',
    targetSelector: '[data-guide="nav-catalog"]',
  },
  {
    id: 'pro-gis-import',
    page: 'map',
    title: '10. QGIS & GeoPackage Import',
    shortDesc:
      'Importér .gpkg, Shapefiler eller GeoJSON direkte fra QGIS til kundens telefon og arbejdsplads.',
    tip: '🗺️ Opret præcise registreringer i QGIS og lad kunden bruge dem direkte som GPS-arbejdsredskab.',
    actionLabel: 'Gå til Kortet',
    actionType: 'navigate',
    targetSelector: '[data-guide="action-gis-import"]',
  },
  {
    id: 'pro-pdf',
    page: 'plans',
    title: '11. Executive Revirplan PDF Print',
    shortDesc:
      'Generér højopløselige revirplaner med målestoksforhold, tegnforklaring og UTM-koordinater til print.',
    tip: '🖨️ Perfekt til ophæng i jagtstuen eller vedlæg til konsortiemødet og lodsejeren.',
    actionLabel: 'Se Plejeplaner',
    actionType: 'navigate',
    targetSelector: '[data-guide="nav-plans"]',
  },
];

export interface UserOnboardingGuideProps {
  isOpen: boolean;
  onClose: () => void;
  userTier: GuideTier;
  onNavigate: (page: Page) => void;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
  onTriggerDraw?: (req: { kind: 'field' | 'zone'; areaType: AreaType }) => void;
  onOpenUpgradeModal?: (featureName: string, tier: 'semipro' | 'pro', desc?: string) => void;
  onStartInteractiveTour: (tier: GuideTier) => void;
}

export default function UserOnboardingGuide({
  isOpen,
  onClose,
  userTier,
  onNavigate,
  onToggleFullscreen,
  isFullscreen,
  onTriggerDraw,
  onOpenUpgradeModal,
  onStartInteractiveTour,
}: UserOnboardingGuideProps) {
  const { ref: dialogRef } = useDialogA11y({ open: isOpen, onClose });
  const [selectedTier, setSelectedTier] = useState<GuideTier>(userTier);
  const [stepIndex, setStepIndex] = useState(0);

  // Sync selected tier with userTier when opened
  useEffect(() => {
    if (isOpen) {
      setSelectedTier(userTier);
      setStepIndex(0);
    }
  }, [isOpen, userTier]);

  if (!isOpen) return null;

  const currentSteps =
    selectedTier === 'pro'
      ? GUIDE_STEPS_PRO
      : selectedTier === 'semipro'
        ? GUIDE_STEPS_SEMIPRO
        : GUIDE_STEPS_FREE;

  const currentStep = currentSteps[stepIndex] || currentSteps[0];
  const isUserOwnTier = selectedTier === userTier;
  const isUpgradable =
    (userTier === 'free' && (selectedTier === 'semipro' || selectedTier === 'pro')) ||
    (userTier === 'semipro' && selectedTier === 'pro');

  const handleNext = () => {
    if (stepIndex < currentSteps.length - 1) {
      setStepIndex((i) => i + 1);
    }
  };

  const handlePrev = () => {
    if (stepIndex > 0) {
      setStepIndex((i) => i - 1);
    }
  };

  const handleAction = (step: GuideStep) => {
    if (!step.actionType) return;
    if (step.actionType === 'fullscreen') {
      onToggleFullscreen?.();
    } else if (step.actionType === 'draw_field') {
      onNavigate('map');
      onTriggerDraw?.({ kind: 'field', areaType: 'mark' });
      onClose();
    } else if (step.actionType === 'navigate') {
      onNavigate(step.page);
      onClose();
    }
  };

  return (
    <div
      ref={dialogRef}
      className="upgrade-modal-backdrop guide-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="guide-modal-title"
      onClick={onClose}
    >
      <div
        className="upgrade-modal-card guide-modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(640px, 100%)', padding: '24px 26px' }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: '#eef5ea',
                color: '#1b3b22',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Compass size={22} />
            </div>
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  color: '#4b7554',
                  textTransform: 'uppercase',
                }}
              >
                Plotwise Brugerguide &amp; Introduktion
              </div>
              <h2
                id="guide-modal-title"
                style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1b3b22' }}
              >
                Kom godt i gang med dit revir
              </h2>
            </div>
          </div>
          <button
            type="button"
            className="upgrade-modal-close"
            onClick={onClose}
            aria-label="Luk guide"
            style={{ position: 'static' }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Tier Selector Tabs */}
        <div
          style={{
            display: 'flex',
            background: '#f2f6ee',
            padding: 4,
            borderRadius: 10,
            gap: 4,
            marginBottom: 20,
          }}
        >
          <button
            type="button"
            className="guide-tier-tab"
            onClick={() => {
              setSelectedTier('free');
              setStepIndex(0);
            }}
            style={{
              flex: 1,
              padding: '8px 10px',
              borderRadius: 7,
              border: 'none',
              background: selectedTier === 'free' ? '#ffffff' : 'transparent',
              color: selectedTier === 'free' ? '#1b3b22' : '#576b5c',
              fontWeight: selectedTier === 'free' ? 700 : 500,
              fontSize: 12.5,
              cursor: 'pointer',
              boxShadow: selectedTier === 'free' ? '0 2px 6px rgba(0,0,0,0.06)' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              transition: 'all 0.15s ease',
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#2e7d32',
                display: 'inline-block',
                flexShrink: 0,
              }}
            />
            <span className="guide-tier-tab-text-desktop">Almindelig</span>
            <span className="guide-tier-tab-text-mobile">Alm.</span>
            {userTier === 'free' && (
              <span
                style={{
                  fontSize: 9.5,
                  padding: '1px 5px',
                  borderRadius: 4,
                  background: '#e3efe0',
                  color: '#1b3b22',
                  flexShrink: 0,
                }}
              >
                Din plan
              </span>
            )}
          </button>

          <button
            type="button"
            className="guide-tier-tab"
            onClick={() => {
              setSelectedTier('semipro');
              setStepIndex(0);
            }}
            style={{
              flex: 1,
              padding: '8px 10px',
              borderRadius: 7,
              border: 'none',
              background: selectedTier === 'semipro' ? '#ffffff' : 'transparent',
              color: selectedTier === 'semipro' ? '#1b3b22' : '#576b5c',
              fontWeight: selectedTier === 'semipro' ? 700 : 500,
              fontSize: 12.5,
              cursor: 'pointer',
              boxShadow: selectedTier === 'semipro' ? '0 2px 6px rgba(0,0,0,0.06)' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              transition: 'all 0.15s ease',
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#f59e0b',
                display: 'inline-block',
                flexShrink: 0,
              }}
            />
            <span className="guide-tier-tab-text-desktop">Semi-pro (49,-)</span>
            <span className="guide-tier-tab-text-mobile">Semi-pro</span>
            {userTier === 'semipro' && (
              <span
                style={{
                  fontSize: 9.5,
                  padding: '1px 5px',
                  borderRadius: 4,
                  background: '#fef3c7',
                  color: '#92400e',
                  flexShrink: 0,
                }}
              >
                Din plan
              </span>
            )}
          </button>

          <button
            type="button"
            className="guide-tier-tab"
            onClick={() => {
              setSelectedTier('pro');
              setStepIndex(0);
            }}
            style={{
              flex: 1,
              padding: '8px 10px',
              borderRadius: 7,
              border: 'none',
              background: selectedTier === 'pro' ? '#ffffff' : 'transparent',
              color: selectedTier === 'pro' ? '#1b3b22' : '#576b5c',
              fontWeight: selectedTier === 'pro' ? 700 : 500,
              fontSize: 12.5,
              cursor: 'pointer',
              boxShadow: selectedTier === 'pro' ? '0 2px 6px rgba(0,0,0,0.06)' : 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              transition: 'all 0.15s ease',
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#2563eb',
                display: 'inline-block',
                flexShrink: 0,
              }}
            />
            <span className="guide-tier-tab-text-desktop">Professionel</span>
            <span className="guide-tier-tab-text-mobile">Pro</span>
            {userTier === 'pro' && (
              <span
                style={{
                  fontSize: 9.5,
                  padding: '1px 5px',
                  borderRadius: 4,
                  background: '#dbeafe',
                  color: '#1e40af',
                }}
              >
                Din plan
              </span>
            )}
          </button>
        </div>

        {/* Interactive Tour CTA Banner */}
        <div
          style={{
            background: 'linear-gradient(135deg, #1b3b22 0%, #295333 100%)',
            color: '#ffffff',
            borderRadius: 10,
            padding: '12px 16px',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 2 }}>
              {isUserOwnTier
                ? 'Vil du prøve den interaktive tur i dit eget panel?'
                : `Prøv den interaktive tur for ${selectedTier === 'pro' ? 'Professionel' : 'Semi-Pro'}?`}
            </div>
            <div style={{ fontSize: 12, opacity: 0.9 }}>
              {isUserOwnTier
                ? 'Vi guider dig igennem menuerne og lader dig oprette og tegne dit første revir.'
                : 'Følg pilene og se præcis, hvor funktionerne findes og hvordan de fungerer.'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {!isUserOwnTier && isUpgradable && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onOpenUpgradeModal?.(
                    selectedTier === 'semipro' ? 'Semi-Pro Opgradering' : 'Professionel Rådgiver',
                    selectedTier === 'semipro' ? 'semipro' : 'pro',
                  );
                }}
                style={{
                  background: 'rgba(255, 255, 255, 0.18)',
                  color: '#ffffff',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  fontWeight: 600,
                  fontSize: 12,
                  padding: '8px 12px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Opgrader
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                onClose();
                onStartInteractiveTour(selectedTier);
              }}
              style={{
                background: '#a3c27e',
                color: '#1b3b22',
                border: 'none',
                fontWeight: 700,
                fontSize: 12.5,
                padding: '9px 15px',
                borderRadius: 8,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                whiteSpace: 'nowrap',
                boxShadow: '0 3px 8px rgba(0,0,0,0.2)',
              }}
            >
              <Play size={15} fill="#1b3b22" />
              Start tur
            </button>
          </div>
        </div>

        {/* Step Progress Indicators */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {currentSteps.map((step, idx) => (
            <button
              key={step.id}
              type="button"
              onClick={() => setStepIndex(idx)}
              style={{
                flex: 1,
                height: 5,
                borderRadius: 4,
                background: idx === stepIndex ? '#1b3b22' : idx < stepIndex ? '#82a875' : '#e0e7db',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                transition: 'background 0.2s ease',
              }}
              title={`Gå til trin ${idx + 1}: ${step.title}`}
            />
          ))}
        </div>

        {/* Step Content Card */}
        <div
          style={{
            background: '#ffffff',
            border: '1px solid #e1e7dc',
            borderRadius: 10,
            padding: '18px 20px',
            marginBottom: 20,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 6,
            }}
          >
            <span
              style={{ fontSize: 11, fontWeight: 700, color: '#4b7554', letterSpacing: '0.04em' }}
            >
              TRIN {stepIndex + 1} AF {currentSteps.length}
            </span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: '#657766',
                background: '#f2f6ee',
                padding: '2px 8px',
                borderRadius: 4,
              }}
            >
              Fane: {currentStep.page}
            </span>
          </div>

          <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: '#1b3b22' }}>
            {currentStep.title}
          </h3>

          <p style={{ margin: '0 0 12px', fontSize: 13.5, color: '#334135', lineHeight: 1.5 }}>
            {currentStep.shortDesc}
          </p>

          <div
            style={{
              background: '#f8faf6',
              borderLeft: '3px solid #6b9e59',
              padding: '10px 12px',
              borderRadius: '0 6px 6px 0',
              fontSize: 12.5,
              color: '#28462b',
              lineHeight: 1.45,
            }}
          >
            {currentStep.tip}
          </div>

          {currentStep.actionLabel && isUserOwnTier && (
            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-start' }}>
              <button
                type="button"
                onClick={() => handleAction(currentStep)}
                style={{
                  background: '#f0f5ec',
                  color: '#1b3b22',
                  border: '1px solid #c2d8b8',
                  padding: '7px 13px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {currentStep.actionType === 'fullscreen' ? (
                  <Maximize2 size={13} />
                ) : currentStep.actionType === 'draw_field' ? (
                  <LandPlot size={13} />
                ) : (
                  <ArrowRight size={13} />
                )}
                {currentStep.actionLabel}
              </button>
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button
            type="button"
            onClick={handlePrev}
            disabled={stepIndex === 0}
            style={{
              background: '#f7faf5',
              border: '1px solid #d8e2d4',
              color: stepIndex === 0 ? '#b0c0ac' : '#223c26',
              borderRadius: 8,
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: stepIndex === 0 ? 'default' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <ArrowLeft size={15} />
            Forrige
          </button>

          <span style={{ fontSize: 12, color: '#687b6a', fontWeight: 500 }}>
            {stepIndex + 1} / {currentSteps.length}
          </span>

          {stepIndex < currentSteps.length - 1 ? (
            <button
              type="button"
              onClick={handleNext}
              style={{
                background: '#1b3b22',
                border: 'none',
                color: '#ffffff',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              }}
            >
              Næste
              <ArrowRight size={15} />
            </button>
          ) : (
            <button
              type="button"
              onClick={onClose}
              style={{
                background: '#2b5a32',
                border: 'none',
                color: '#ffffff',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <CheckCircle2 size={15} />
              Luk guide
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Spotlight Pointer & Glowing Beacon pointing to active UI features
 */
export interface GuideTargetPointerProps {
  targetSelector?: string;
  label?: string;
  onClickTarget?: () => void;
}

export function GuideTargetPointer({
  targetSelector,
  label = 'Prøv denne funktion her',
  onClickTarget,
}: GuideTargetPointerProps) {
  const [rect, setRect] = useState<DOMRect | null>(null);

  // On mobile screens (<768px), disable external floating pointers to keep mobile canvas 100% clean and free of clutter
  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;
  if (isMobile) return null;

  useEffect(() => {
    if (!targetSelector) {
      setRect(null);
      return;
    }

    const updateRect = () => {
      try {
        let selector = targetSelector;
        const isMobileScreen = window.innerWidth < 768;
        if (isMobileScreen) {
          if (selector === '[data-guide="nav-overview"]') {
            selector = '[data-guide="mobile-nav-map"]';
          } else if (selector === '[data-guide="action-vildtager"]') {
            selector = '[data-guide="mobile-action-draw"], [data-guide="action-vildtager"]';
          } else if (selector === '[data-guide="nav-catalog"]') {
            selector = '[data-guide="mobile-nav-menu"]';
          }
        }

        let el = document.querySelector(selector);
        if (!el && selector !== targetSelector) {
          el = document.querySelector(targetSelector);
        }

        if (el) {
          const r = el.getBoundingClientRect();
          const winW = window.innerWidth || document.documentElement.clientWidth;
          const winH = window.innerHeight || document.documentElement.clientHeight;
          // Target must be visible and actually on screen
          const isVisible =
            r.width > 0 &&
            r.height > 0 &&
            r.top >= -10 &&
            r.bottom <= winH + 10 &&
            r.left >= -10 &&
            r.right <= winW + 10;

          if (isVisible) {
            setRect(r);
            return;
          }
        }
      } catch {}
      setRect(null);
    };

    updateRect();
    const interval = setInterval(updateRect, 300);
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
    };
  }, [targetSelector]);

  if (!rect) return null;

  // Decide pointer badge placement (prefer below if room, otherwise above, or to right if sidebar)
  const isSidebar = rect.left < 180;
  const isNearTop = rect.top < 130;

  let badgeStyle: React.CSSProperties = {};
  let arrowClass = 'arrow-up';
  let ArrowComponent = ArrowUp;

  if (isSidebar) {
    badgeStyle = {
      top: Math.max(10, rect.top + rect.height / 2 - 16),
      left: rect.right + 12,
    };
    arrowClass = 'arrow-left';
    ArrowComponent = ArrowLeft;
  } else if (isNearTop) {
    badgeStyle = {
      top: rect.bottom + 10,
      left: Math.max(10, rect.left + rect.width / 2 - 80),
    };
    arrowClass = 'arrow-up';
    ArrowComponent = ArrowUp;
  } else {
    badgeStyle = {
      top: Math.max(10, rect.top - 42),
      left: Math.max(10, rect.left + rect.width / 2 - 80),
    };
    arrowClass = 'arrow-down';
    ArrowComponent = ArrowDown;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onClickTarget) {
      onClickTarget();
    } else if (targetSelector) {
      const el = document.querySelector(targetSelector) as HTMLElement | null;
      el?.click();
    }
  };

  const isMobileScreen = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

  return (
    <>
      <div
        className="guide-spotlight-ring"
        style={{
          top: rect.top - 4,
          left: rect.left - 4,
          width: rect.width + 8,
          height: rect.height + 8,
        }}
      />
      {!isMobileScreen && (
        <div
          className={`guide-pointer-badge ${arrowClass}`}
          style={badgeStyle}
          onClick={handleClick}
          title="Klik for at afprøve denne funktion"
        >
          <ArrowComponent size={14} />
          <span>{label}</span>
        </div>
      )}
    </>
  );
}

/**
 * Floating Spotlight Banner for in-app live tour with drag support
 */
export interface FloatingTourBarProps {
  currentTier: GuideTier;
  stepIndex: number;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
  onAction?: (actionType: string) => void;
  isFullscreen?: boolean;
  collapsed?: boolean;
  onToggleCollapse?: (collapsed: boolean) => void;
  tracking?: TourTrackingProps;
  isMobile?: boolean;
}

export function FloatingTourBar({
  currentTier,
  stepIndex,
  onNext,
  onPrev,
  onClose,
  onAction,
  isFullscreen,
  collapsed: propCollapsed,
  onToggleCollapse,
  tracking,
  isMobile: propIsMobile,
}: FloatingTourBarProps) {
  const steps =
    currentTier === 'pro'
      ? GUIDE_STEPS_PRO
      : currentTier === 'semipro'
        ? GUIDE_STEPS_SEMIPRO
        : GUIDE_STEPS_FREE;

  const current = steps[stepIndex] || steps[0];
  const isLast = stepIndex === steps.length - 1;

  // Detect mobile vs desktop viewport
  const [internalIsMobile, setInternalIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 880 : false,
  );
  const isMobile = propIsMobile !== undefined ? propIsMobile : internalIsMobile;
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const collapsed = propCollapsed !== undefined ? propCollapsed : internalCollapsed;
  const setCollapsed = (val: boolean | ((prev: boolean) => boolean)) => {
    const nextVal = typeof val === 'function' ? val(collapsed) : val;
    if (onToggleCollapse) onToggleCollapse(nextVal);
    else setInternalCollapsed(nextVal);
  };

  const isDrawing = Boolean(tracking?.isDrawing);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => {
      setInternalIsMobile(window.innerWidth <= 880);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Draggable position state for desktop
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null);
  const [isMoved, setIsMoved] = useState(false);
  const barRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; initX: number; initY: number } | null>(
    null,
  );

  // Initialize position to bottom center on desktop
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const computeInitial = () => {
      const width = Math.min(600, window.innerWidth - 32);
      const initX = Math.max(16, (window.innerWidth - width) / 2);
      const initY = Math.max(20, window.innerHeight - 250);
      return { x: initX, y: initY };
    };

    if (!position) {
      setPosition(computeInitial());
    }
  }, [position]);

  const handleDragStart = (clientX: number, clientY: number) => {
    if (!position || isMobile) return;
    dragRef.current = {
      startX: clientX,
      startY: clientY,
      initX: position.x,
      initY: position.y,
    };

    const handleMove = (e: MouseEvent) => {
      if (!dragRef.current || !barRef.current) return;
      const dx = e.clientX - dragRef.current.startX;
      const dy = e.clientY - dragRef.current.startY;
      const rect = barRef.current.getBoundingClientRect();
      const maxX = window.innerWidth - rect.width - 12;
      const maxY = window.innerHeight - rect.height - 12;
      const nextX = Math.max(12, Math.min(maxX, dragRef.current.initX + dx));
      const nextY = Math.max(12, Math.min(maxY, dragRef.current.initY + dy));
      setPosition({ x: nextX, y: nextY });
      setIsMoved(true);
    };

    const handleUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  const handleResetPosition = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (typeof window === 'undefined') return;
    const width = Math.min(600, window.innerWidth - 32);
    setPosition({
      x: Math.max(16, (window.innerWidth - width) / 2),
      y: Math.max(20, window.innerHeight - 250),
    });
    setIsMoved(false);
  };

  // Touch swipe handling for mobile collapse / expand
  const touchStartY = useRef<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartY.current === null) return;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    touchStartY.current = null;
    if (deltaY > 35 && !collapsed) {
      setCollapsed(true);
    } else if (deltaY < -30 && collapsed) {
      setCollapsed(false);
    }
  };

  // Reaktiv auto-advancement tracking
  const [stepEnteredAt, setStepEnteredAt] = useState(() => Date.now());
  const [autoAdvanceSuccess, setAutoAdvanceSuccess] = useState<string | null>(null);
  const initialsRef = useRef<{
    fieldCount: number;
    hasZoneField: boolean;
    hasPoi: boolean;
    cadastral: boolean;
    markblokke: boolean;
  }>({
    fieldCount: tracking?.fieldCount ?? 0,
    hasZoneField: Boolean(tracking?.hasZoneField),
    hasPoi: Boolean(tracking?.hasPoi),
    cadastral: Boolean(tracking?.cadastralEnabled),
    markblokke: Boolean(tracking?.markblokkeEnabled),
  });

  useEffect(() => {
    setStepEnteredAt(Date.now());
    setAutoAdvanceSuccess(null);
    if (tracking) {
      initialsRef.current = {
        fieldCount: tracking.fieldCount,
        hasZoneField: Boolean(tracking.hasZoneField),
        hasPoi: Boolean(tracking.hasPoi),
        cadastral: Boolean(tracking.cadastralEnabled),
        markblokke: Boolean(tracking.markblokkeEnabled),
      };
    }
  }, [stepIndex]);

  useEffect(() => {
    if (!tracking || autoAdvanceSuccess) return;

    const timeInStep = Date.now() - stepEnteredAt;
    const currentStepObj = steps[stepIndex];
    if (!currentStepObj) return;

    let advanceReason: string | null = null;

    if (currentTier === 'free') {
      if (currentStepObj.id === 'free-overview') {
        if (tracking.page === 'map') advanceReason = 'Kortet er åbnet';
      } else if (currentStepObj.id === 'free-map-fullscreen') {
        if ((tracking.page === 'map' && timeInStep > 1500) || isFullscreen || tracking.isDrawing) {
          advanceReason = 'Kortet er klar';
        }
      } else if (currentStepObj.id === 'free-map-draw') {
        if (tracking.fieldCount > initialsRef.current.fieldCount) {
          advanceReason = 'Vildtager optegnet!';
        }
      } else if (currentStepObj.id === 'free-map-layers') {
        if (tracking.page === 'catalog' || tracking.cadastralEnabled) {
          advanceReason = 'Kortlag udforsket';
        }
      } else if (currentStepObj.id === 'free-catalog') {
        if (tracking.selectedFieldHasSeed || (tracking.cartCount && tracking.cartCount > 0)) {
          advanceReason = 'Frøblanding valgt!';
        }
      } else if (currentStepObj.id === 'free-advisor') {
        if (tracking.page === 'advisor') {
          advanceReason = 'Rådgivning åbnet';
        }
      }
    } else if (currentTier === 'semipro') {
      if (currentStepObj.id === 'semi-overview') {
        if (tracking.page === 'map') advanceReason = 'Kortet er åbnet';
      } else if (currentStepObj.id === 'semi-map-fullscreen') {
        if ((tracking.page === 'map' && timeInStep > 1500) || isFullscreen || tracking.isDrawing) {
          advanceReason = 'Kortet er klar';
        }
      } else if (currentStepObj.id === 'semi-cadastral') {
        if (tracking.cadastralEnabled && !initialsRef.current.cadastral) {
          advanceReason = 'Matrikelskel aktiveret!';
        }
      } else if (currentStepObj.id === 'semi-map-draw') {
        if (tracking.fieldCount > initialsRef.current.fieldCount) {
          advanceReason = 'Vildtager optegnet!';
        }
      } else if (currentStepObj.id === 'semi-zones') {
        if (tracking.hasZoneField && !initialsRef.current.hasZoneField) {
          advanceReason = 'Biotopzone oprettet!';
        }
      } else if (currentStepObj.id === 'semi-poi') {
        if (tracking.hasPoi && !initialsRef.current.hasPoi) {
          advanceReason = 'Jagt-POI oprettet!';
        }
      } else if (currentStepObj.id === 'semi-seeding') {
        if (tracking.page === 'seeding' && timeInStep > 1200) {
          advanceReason = 'Så-barometer åbnet';
        }
      } else if (currentStepObj.id === 'semi-plans') {
        if (tracking.page === 'plans' && timeInStep > 1200) {
          advanceReason = 'Plejeplaner åbnet';
        }
      } else if (currentStepObj.id === 'semi-season-plans') {
        if (tracking.page === 'plans' && timeInStep > 1200) {
          advanceReason = 'Sæsonplaner åbnet';
        }
      } else if (currentStepObj.id === 'semi-catalog') {
        if (tracking.selectedFieldHasSeed || (tracking.cartCount && tracking.cartCount > 0)) {
          advanceReason = 'Frøblanding valgt!';
        }
      }
    } else if (currentTier === 'pro') {
      if (currentStepObj.id === 'pro-overview') {
        if (tracking.page === 'map') advanceReason = 'Arbejdsbord åbnet';
      } else if (currentStepObj.id === 'pro-map-fullscreen') {
        if ((tracking.page === 'map' && timeInStep > 1500) || isFullscreen || tracking.isDrawing) {
          advanceReason = 'Arbejdsbord klar';
        }
      } else if (currentStepObj.id === 'pro-markblokke') {
        if (tracking.markblokkeEnabled && !initialsRef.current.markblokke) {
          advanceReason = 'Markblokke aktiveret!';
        }
      } else if (currentStepObj.id === 'pro-cadastral') {
        if (tracking.cadastralEnabled && !initialsRef.current.cadastral) {
          advanceReason = 'Matrikelskel aktiveret!';
        }
      } else if (currentStepObj.id === 'pro-precision-draw') {
        if (tracking.fieldCount > initialsRef.current.fieldCount) {
          advanceReason = 'Præcisionsparcel optegnet!';
        }
      } else if (currentStepObj.id === 'pro-zones-buffer') {
        if (tracking.hasZoneField && !initialsRef.current.hasZoneField) {
          advanceReason = 'Læhegn/remise oprettet!';
        }
      } else if (currentStepObj.id === 'pro-biotope') {
        if (tracking.page === 'plans' && timeInStep > 1200) {
          advanceReason = 'Biotopplan åbnet';
        }
      } else if (currentStepObj.id === 'pro-clients') {
        if (tracking.page === 'clients' && timeInStep > 1200) {
          advanceReason = 'Klientstyring åbnet';
        }
      } else if (currentStepObj.id === 'pro-catalog-machine') {
        if (tracking.page === 'catalog' && timeInStep > 1200) {
          advanceReason = 'Frø- og maskinoptimering åbnet';
        }
      } else if (currentStepObj.id === 'pro-gis-import') {
        if (tracking.page === 'map' && timeInStep > 1200) {
          advanceReason = 'QGIS Import klar';
        }
      } else if (currentStepObj.id === 'pro-pdf') {
        if (tracking.page === 'plans' && timeInStep > 1200) {
          advanceReason = 'Revirplan klar til print';
        }
      }
    }

    if (advanceReason) {
      setAutoAdvanceSuccess(advanceReason);
      const timer = setTimeout(() => {
        setAutoAdvanceSuccess(null);
        if (!isLast) {
          onNext();
        }
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [
    tracking,
    stepIndex,
    stepEnteredAt,
    autoAdvanceSuccess,
    currentTier,
    steps,
    isFullscreen,
    isLast,
    onNext,
  ]);

  const isDrawingChip = isMobile && isDrawing;

  const barStyle: React.CSSProperties = isMobile
    ? isDrawingChip
      ? {
          position: 'fixed',
          zIndex: 'calc(var(--z-modal) + 60)',
          bottom: 'calc(64px + env(safe-area-inset-bottom, 0px))',
          left: 12,
          right: 12,
          margin: '0 auto',
          maxWidth: 'calc(100vw - 24px)',
          background: '#ffffff',
          borderRadius: 24,
          border: '1.5px solid #2d5a36',
          boxShadow: '0 4px 18px rgba(27, 59, 34, 0.22)',
          padding: '4px 10px',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          animation: 'slideUpTour 0.2s ease-out',
        }
      : {
          position: 'fixed',
          zIndex: 'calc(var(--z-modal) + 60)',
          bottom: 'calc(64px + env(safe-area-inset-bottom, 0px))',
          left: 0,
          right: 0,
          width: '100%',
          maxWidth: '100vw',
          background: '#ffffff',
          borderRadius: '16px 16px 0 0',
          boxShadow: '0 -8px 30px rgba(16, 36, 28, 0.18)',
          borderTop: '2px solid #2d5a36',
          borderLeft: 'none',
          borderRight: 'none',
          borderBottom: 'none',
          padding: collapsed ? '8px 12px' : '12px 14px',
          boxSizing: 'border-box',
          animation: 'slideUpTour 0.22s ease-out',
        }
    : {
        position: 'fixed',
        zIndex: 'calc(var(--z-modal) + 60)',
        width: 'min(620px, calc(100vw - 32px))',
        background: '#ffffff',
        borderRadius: 12,
        boxShadow: '0 16px 45px rgba(16, 36, 28, 0.32), 0 4px 12px rgba(0,0,0,0.1)',
        border: '1.5px solid #2d5a36',
        padding: '10px 18px 16px',
        boxSizing: 'border-box',
        animation: 'slideUpTour 0.22s ease-out',
        ...(position
          ? { left: position.x, top: position.y }
          : { bottom: 20, left: '50%', transform: 'translateX(-50%)' }),
      };

  return (
    <>
      {/* Visual Pointer / Glowing beacon on active feature */}
      <GuideTargetPointer
        targetSelector={current.targetSelector}
        label={current.actionLabel || 'Prøv denne funktion'}
        onClickTarget={() => current.actionType && onAction?.(current.actionType)}
      />

      <aside
        ref={barRef}
        className={`floating-tour-bar ${isMobile ? 'is-mobile' : ''} ${collapsed ? 'is-collapsed' : ''} ${isDrawingChip ? 'is-drawing-chip' : ''}`}
        role="region"
        aria-label="Aktiv arbejdsplads-guide"
        style={barStyle}
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
      >
        {isDrawingChip ? (
          <div
            className="mobile-guide-drawing-chip"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              width: '100%',
              minHeight: 44,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                flex: 1,
                minWidth: 0,
                cursor: 'pointer',
              }}
              onClick={() => setCollapsed(false)}
            >
              <span className="drawing-pulse-dot" />
              <Compass size={15} color="#1b3b22" />
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#172b1d',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                Tegnetilstand aktiv · Trin {stepIndex + 1}/{steps.length}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              aria-label="Vis guide"
              style={{
                background: '#e9f3e4',
                color: '#1b3b22',
                border: '1px solid #c2d8b8',
                borderRadius: 8,
                padding: '0 12px',
                fontSize: 11.5,
                fontWeight: 700,
                cursor: 'pointer',
                minHeight: 44,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                flexShrink: 0,
              }}
            >
              <span>Guide</span>
              <ChevronUp size={14} />
            </button>
          </div>
        ) : isMobile && collapsed ? (
          <div
            className="mobile-guide-collapsed-row"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              aria-label="Udvid guide"
              style={{
                background: '#e9f3e4',
                color: '#1b3b22',
                border: '1px solid #c2d8b8',
                borderRadius: 20,
                padding: '6px 12px',
                fontSize: 11.5,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                minHeight: 44,
                flex: 1,
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <span style={{ flexShrink: 0, textTransform: 'uppercase' }}>
                {currentTier === 'pro' ? 'Pro' : currentTier === 'semipro' ? 'Semi-pro' : 'Guide'} ·{' '}
                {stepIndex + 1}/{steps.length}
              </span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {current.title}
              </span>
              <ChevronUp size={15} style={{ marginLeft: 'auto', flexShrink: 0 }} />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {stepIndex > 0 && (
                <button
                  type="button"
                  onClick={onPrev}
                  aria-label="Forrige trin"
                  style={{
                    background: '#f2f6ee',
                    border: '1px solid #d5dfd1',
                    borderRadius: 8,
                    minWidth: 44,
                    minHeight: 44,
                    display: 'grid',
                    placeItems: 'center',
                    cursor: 'pointer',
                    color: '#28462b',
                  }}
                >
                  <ArrowLeft size={16} />
                </button>
              )}

              <button
                type="button"
                onClick={isLast ? onClose : onNext}
                aria-label={isLast ? 'Afslut guide' : 'Næste trin'}
                style={{
                  background: '#1b382b',
                  border: 'none',
                  color: '#ffffff',
                  borderRadius: 8,
                  minHeight: 44,
                  padding: '0 14px',
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                {isLast ? <CheckCircle2 size={16} /> : <ArrowRight size={16} />}
                <span>{isLast ? 'Færdig' : 'Næste'}</span>
              </button>

              <button
                type="button"
                onClick={onClose}
                aria-label="Luk guide"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#64748b',
                  borderRadius: 8,
                  minWidth: 44,
                  minHeight: 44,
                  display: 'grid',
                  placeItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <X size={18} />
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Top Drag Handle / Mobile Collapse Pill */}
            <div
              className="tour-drag-handle"
              onMouseDown={isMobile ? undefined : (e) => handleDragStart(e.clientX, e.clientY)}
              onClick={isMobile ? () => setCollapsed(true) : undefined}
              title={isMobile ? 'Tryk for at minimere guiden' : 'Træk for at flytte guiden'}
            >
              {isMobile ? (
                <div className="tour-drag-pill" />
              ) : (
                <>
                  <div className="tour-drag-dots" aria-hidden="true">
                    <span className="tour-drag-dot" />
                    <span className="tour-drag-dot" />
                    <span className="tour-drag-dot" />
                  </div>
                  <span
                    style={{
                      fontSize: 10.5,
                      color: '#64748b',
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Træk for at flytte
                  </span>
                  {isMoved && (
                    <button
                      type="button"
                      onClick={handleResetPosition}
                      style={{
                        background: '#f1f5f9',
                        border: '1px solid #cbd5e1',
                        borderRadius: 4,
                        padding: '1px 6px',
                        fontSize: 10,
                        color: '#475569',
                        cursor: 'pointer',
                        marginLeft: 4,
                      }}
                      title="Nulstil guiden til bunden af skærmen"
                    >
                      ↺ Nulstil
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Auto-advance Success Indicator */}
            {autoAdvanceSuccess && (
              <div
                style={{
                  background: '#e9f5e6',
                  color: '#1b3b22',
                  border: '1px solid #a4d29b',
                  borderRadius: 7,
                  padding: '5px 10px',
                  fontSize: 12,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginBottom: 8,
                }}
              >
                <CheckCircle2 size={15} color="#2e7d32" />
                <span>✓ {autoAdvanceSuccess} — går videre til næste trin…</span>
              </div>
            )}

            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 6,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, flex: 1 }}>
                <span
                  style={{
                    background: '#e9f3e4',
                    color: '#1b3b22',
                    fontSize: 10.5,
                    fontWeight: 700,
                    padding: '2px 7px',
                    borderRadius: 4,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    flexShrink: 0,
                  }}
                >
                  {currentTier === 'pro' ? 'Pro' : currentTier === 'semipro' ? 'Semi-Pro' : 'Guide'}{' '}
                  · Trin {stepIndex + 1} af {steps.length}
                </span>
                <strong
                  style={{
                    fontSize: 13.5,
                    color: '#172b1d',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {current.title}
                </strong>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                {isMobile && (
                  <button
                    type="button"
                    onClick={() => setCollapsed(true)}
                    aria-label="Minimer guide"
                    title="Minimer guide"
                    style={{
                      background: '#f1f5f9',
                      border: '1px solid #e2e8f0',
                      color: '#475569',
                      borderRadius: 8,
                      minWidth: 44,
                      minHeight: 44,
                      display: 'grid',
                      placeItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <ChevronDown size={18} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Luk guide"
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#657766',
                    cursor: 'pointer',
                    borderRadius: 8,
                    minWidth: 44,
                    minHeight: 44,
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <p style={{ margin: '0 0 8px', fontSize: 12.5, color: '#334135', lineHeight: 1.45 }}>
              {current.shortDesc}
            </p>

            <div
              style={{
                background: '#f8f9f6',
                borderLeft: '3px solid #2d5a36',
                padding: '7px 10px',
                borderRadius: '0 6px 6px 0',
                fontSize: 11.5,
                color: '#172b1d',
                lineHeight: 1.4,
                marginBottom: 10,
              }}
            >
              {current.tip}
            </div>

            {/* Action CTA Button */}
            {current.actionLabel && onAction && (
              <div style={{ marginBottom: 10 }}>
                <button
                  type="button"
                  onClick={() => current.actionType && onAction(current.actionType)}
                  style={{
                    width: isMobile ? '100%' : 'auto',
                    background: '#1b382b',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: 8,
                    minHeight: 44,
                    padding: '8px 16px',
                    fontSize: 12.5,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                  }}
                >
                  {current.actionType === 'fullscreen' ? (
                    isFullscreen ? (
                      <Minimize2 size={15} />
                    ) : (
                      <Maximize2 size={15} />
                    )
                  ) : current.actionType === 'draw_field' ? (
                    <LandPlot size={15} />
                  ) : (
                    <Sparkles size={15} color="#fde047" />
                  )}
                  <span>
                    {current.actionType === 'fullscreen'
                      ? isFullscreen
                        ? 'Afslut fuldskærm'
                        : 'Kør Fuldskærm (Anbefalet)'
                      : current.actionLabel || 'Prøv denne funktion'}
                  </span>
                </button>
              </div>
            )}

            {/* Completion Banner on last step */}
            {isLast && (
              <div
                style={{
                  background: '#eaf4e6',
                  borderRadius: 8,
                  border: '1px solid #bcdab3',
                  padding: '8px 12px',
                  marginBottom: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <Sparkles size={16} color="#2b5a32" />
                <span style={{ fontSize: 12, fontWeight: 600, color: '#172b1d' }}>
                  Flot klaret! Du har gennemført rundturen. Dit revir er nu klar til vildtpleje.
                </span>
              </div>
            )}

            {/* Footer Navigation */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                marginTop: 6,
              }}
            >
              {stepIndex > 0 ? (
                <button
                  type="button"
                  onClick={onPrev}
                  style={{
                    background: '#f2f6ee',
                    border: '1px solid #d5dfd1',
                    borderRadius: 8,
                    minHeight: 44,
                    padding: '8px 14px',
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: '#172b1d',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  <ArrowLeft size={15} />
                  <span>Forrige</span>
                </button>
              ) : (
                <div />
              )}

              <span style={{ fontSize: 11.5, color: '#657766', fontWeight: 600 }}>
                Trin {stepIndex + 1} af {steps.length}
              </span>

              <button
                type="button"
                onClick={isLast ? onClose : onNext}
                style={{
                  background: '#1b382b',
                  border: 'none',
                  color: '#ffffff',
                  borderRadius: 8,
                  minHeight: 44,
                  padding: '8px 16px',
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                }}
              >
                {isLast ? (
                  <>
                    <CheckCircle2 size={15} />
                    <span>Færdig</span>
                  </>
                ) : (
                  <>
                    <span>Næste</span>
                    <ArrowRight size={15} />
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
}

/**
 * Onboarding banner displayed on dashboard upon first login
 */
export interface DashboardGuideBannerProps {
  onStartGuide: () => void;
  onDismiss: () => void;
  userTier?: GuideTier;
}

export function DashboardGuideBanner({
  onStartGuide,
  onDismiss,
  userTier = 'free',
}: DashboardGuideBannerProps) {
  return (
    <div
      className="dashboard-guide-banner"
      style={{
        background: 'linear-gradient(135deg, #18331e 0%, #254d2e 100%)',
        color: '#ffffff',
        borderRadius: 12,
        padding: '16px 20px',
        marginBottom: 20,
        boxShadow: '0 4px 18px rgba(18, 38, 22, 0.18)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 14,
        border: '1px solid #3b6644',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 260, flex: 1 }}>
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 10,
            background: 'rgba(255, 255, 255, 0.15)',
            color: '#c2e0b6',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <Compass size={24} />
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <strong style={{ fontSize: 15, fontWeight: 700, color: '#ffffff' }}>
              Velkommen til Plotwise!
            </strong>
            <span
              style={{
                fontSize: 10,
                fontWeight: 700,
                background: '#a3c27e',
                color: '#1b3b22',
                padding: '1px 6px',
                borderRadius: 4,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              Kom godt i gang
            </span>
          </div>
          <p style={{ margin: 0, fontSize: 12.5, color: '#e0ebd8', lineHeight: 1.4 }}>
            Tag en hurtig 2-minutters gennemgang af dit revir, lær at optegne vildtagre og beregne
            frøblandinger.
          </p>
        </div>
      </div>

      <div
        className="dashboard-guide-banner-actions"
        style={{ display: 'flex', alignItems: 'center', gap: 10 }}
      >
        <button
          type="button"
          onClick={onStartGuide}
          style={{
            background: '#a3c27e',
            color: '#18331e',
            border: 'none',
            borderRadius: 8,
            padding: '9px 16px',
            fontSize: 13,
            fontWeight: 700,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            boxShadow: '0 3px 8px rgba(0,0,0,0.2)',
            minHeight: 44,
          }}
        >
          <Play size={14} fill="#18331e" />
          Start guide
        </button>

        <button
          type="button"
          onClick={onDismiss}
          aria-label="Skjul guide-banner"
          style={{
            background: 'transparent',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            color: '#d1e0cc',
            borderRadius: 8,
            padding: '8px 12px',
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
            minHeight: 44,
          }}
        >
          Ikke nu
        </button>
      </div>
    </div>
  );
}
